import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import pluralize from 'pluralize';
import { sortBy, isEqual, debounce, map, omit, pick } from 'lodash';
import Pagination from 'components/ui/pagination';
import { defaultFilterValue } from '../project_navigator';
import buildLeadsQueryParams from './build_leads_query_params';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBolt,
  faCaretDown,
  faCaretRight,
  faCheck,
  faDownload,
  faEllipsisV,
  faEnvelope,
  faPencilAlt,
  faPhonePlus,
  faPlus,
  faRectangleHistoryCirclePlus,
  faTrashAlt,
  faUserFriends,
  faUserPlus
} from '@fortawesome/pro-solid-svg-icons';

import {
  approveLeadVaultAccess,
  blockLeadVaultAccess,
  bulkCopyAndConnect,
  bulkRemoveLeads,
  copyAndConnect,
  createBulkTask,
  createCallListWithTasks,
  fetchLeads,
  removeLead,
  revokeLeadVaultAccess,
  showEmailModal,
  showUpdateLeadModal,
  showNewLeadModal,
  showNewTaskModal,
  showLogTaskModal,
  toggleSelectAllLeads,
  toggleSelectLead,
  unblockLeadVaultAccess
} from 'bundles/project/actions/index';
import { setColumns } from '../../actions/session';
import { refreshLead } from 'bundles/project/actions/leads';
import ConnectionLinkModal from 'bundles/project/components/connections/connection_link_modal';
import {
  getAvailableDatabaseOptions,
  getConnectableObjects,
  getProject
} from 'bundles/project/selectors/index';
import { LeadPropTypes } from 'components/project/prop_types';
import PageHeader from 'components/project/page_header';

import LoadingSpinner from 'components/ui/loading_spinner';
import confirm from 'components/confirm';
import BulkTasksModal from 'components/modals/bulk_tasks_modal';
import CsvDownloadButton from 'components/csv_download_button';
import {
  getDecoratedLeads,
  getLeadsMeta,
  getSelectedLeads,
  isFetchingLeads,
  shouldFetchLeads
} from 'bundles/project/selectors/leads';
import AccessOptions from './access_options';
import AddToCallListModal from 'components/modals/add_to_call_list_modal';
import NewCallListModal from 'components/modals/new_call_list_modal';
import LeadsTable from 'bundles/leads/index/table/index';
import LeadName from './lead_name';
import {
  ACCOUNT_STATUS,
  CA_STATUS,
  CA_STATUS_LEASE,
  CA_STATUS_SALE,
  CONFIGURABLE_COLUMNS,
  EXCHANGE_1031,
  EXPIRATION_1031,
  LAST_ACTIVITY,
  LEAD_STATUS,
  NAME,
  PROPERTY,
  SPACE,
  WAITLIST_STATUS,
  DOC_ACCESS_LEVEL_FOR_LEASE,
  DOC_ACCESS_LEVEL_FOR_SALE
} from 'bundles/leads/index/table/cell';
import EmailCaModal from './email_ca_modal';
import NoLeads from 'bundles/leads/shared/no_leads';
import LeadNoResults from 'bundles/leads/shared/no_results';
import LeadFilters from './filters';
import { Dropdown, DropdownButton } from 'react-bootstrap-5';
import { createPortal } from 'react-dom';
import { Button } from 'components/ui/button';
import { useBuildoutOsDisplay } from 'components/buildout_os_display';
import { showSnackbar } from 'components/ui/snackbar/helper';
import InquiryPanel, { hides1031 } from './inquiry_panel';
import { AddContactToListModal } from 'bundles/backoffice/contacts/report/add_contact_to_list_modal';

export const RESULTS_PER_PAGE = 12;
const SEARCH_DEBOUNCE_MS = 300;
const LEASE_SPACE_HIDDEN_COLUMNS = [DOC_ACCESS_LEVEL_FOR_SALE, CA_STATUS_SALE, EXCHANGE_1031, EXPIRATION_1031];

// TODO: needs a more descriptive name - this is more than a listing of leads
// TODO: components should be focused on rendering; domain object manipulation should be moved to class(es)
export class LeadsList extends React.Component {
  static propTypes = {
    approveLeadVaultAccess: PropTypes.func.isRequired,
    availableDatabaseOptions: PropTypes.array.isRequired,
    blockLeadVaultAccess: PropTypes.func.isRequired,
    buildoutOs: PropTypes.bool,
    bulkCopyAndConnect: PropTypes.func.isRequired,
    bulkRemoveLeads: PropTypes.func.isRequired,
    caDownloadPath: PropTypes.string,
    callLists: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired
    })),
    callListsEnabled: PropTypes.bool,
    canDisplayEmailLink: PropTypes.bool.isRequired,
    changeSort: PropTypes.func.isRequired,
    clientReportActive: PropTypes.bool,
    commercialEdgeLogoPath: PropTypes.string.isRequired,
    connectableObjects: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      type: PropTypes.string.isRequired
    })),
    copyAndConnect: PropTypes.func.isRequired,
    createBulkTask: PropTypes.func.isRequired,
    createCallListWithTasks: PropTypes.func.isRequired,
    currentColumns: PropTypes.object,
    currentUser: PropTypes.object,
    databaseMembers: PropTypes.array.isRequired,
    defaultTaskAssignee: PropTypes.object.isRequired,
    emailCa: PropTypes.bool,
    features: PropTypes.shape({
      addLeadsToLists: PropTypes.bool,
      crm: PropTypes.bool
    }).isRequired,
    fetchingLeads: PropTypes.bool,
    fetchLeads: PropTypes.func.isRequired,
    filters: PropTypes.shape({
      caStatusFilter: PropTypes.array.isRequired,
      companyFilter: PropTypes.array.isRequired,
      endDate: PropTypes.object,
      searchText: PropTypes.string.isRequired,
      statusFilter: PropTypes.array.isRequired,
      listingFilter: PropTypes.array.isRequired,
      profileUserTypeFilter: PropTypes.array.isRequired,
      referralSourceFilter: PropTypes.array.isRequired,
      startDate: PropTypes.object,
      waitlistFilter: PropTypes.array.isRequired
    }).isRequired,
    fromDealTab: PropTypes.bool,
    handleFilterChange: PropTypes.func.isRequired,
    hasTasksEnabled: PropTypes.bool.isRequired,
    leadReferralSources: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired
    })),
    leads: PropTypes.arrayOf(LeadPropTypes).isRequired,
    leadsMeta: PropTypes.shape({
      currentPage: PropTypes.number,
      pageSize: PropTypes.number,
      totalCount: PropTypes.number,
      totalPages: PropTypes.number,
      loaded: PropTypes.bool
    }).isRequired,
    leadsPath: PropTypes.string,
    leadStatuses: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired
    })).isRequired,
    leaseSpace: PropTypes.shape({
      listingId: PropTypes.number.isRequired
    }),
    page: PropTypes.number.isRequired,
    project: PropTypes.shape({
      id: PropTypes.number.isRequired,
      canViewUnreleasedFeatures: PropTypes.bool.isRequired,
      caRequiredVaultsEnabled: PropTypes.bool.isRequired,
      condoListings: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        address2: PropTypes.string,
        number: PropTypes.string
      })),
      forwardingEmail: PropTypes.string,
      leaseChecked: PropTypes.bool,
      leaseListingUrl: PropTypes.string,
      leaseSpaceListings: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        address2: PropTypes.string.isRequired
      })),
      lotListings: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        address2: PropTypes.string,
        number: PropTypes.string
      })),
      saleChecked: PropTypes.bool,
      saleListingUrl: PropTypes.string,
      verifiedVaultsEnabled: PropTypes.bool.isRequired,
      waitlistVaultsEnabled: PropTypes.bool.isRequired,
    }).isRequired,
    referralSourceValue: PropTypes.number,
    refreshLead: PropTypes.func.isRequired,
    removeLead: PropTypes.func.isRequired,
    revokeLeadVaultAccess: PropTypes.func.isRequired,
    selectedLeads: PropTypes.arrayOf(PropTypes.array).isRequired,
    setColumns: PropTypes.func.isRequired,
    setPage: PropTypes.func.isRequired,
    shouldFetchLeads: PropTypes.bool.isRequired,
    showEmailModal: PropTypes.func.isRequired,
    showLogTaskModal: PropTypes.func.isRequired,
    showNewLeadModal: PropTypes.func.isRequired,
    showNewTaskModal: PropTypes.func.isRequired,
    showUpdateLeadModal: PropTypes.func.isRequired,
    sort: PropTypes.shape({
      field: PropTypes.string.isRequired,
      reverse: PropTypes.bool.isRequired
    }).isRequired,
    toggleSelectAllLeads: PropTypes.func.isRequired,
    toggleSelectLead: PropTypes.func.isRequired,
    unblockLeadVaultAccess: PropTypes.func.isRequired,
    useVaultGates: PropTypes.bool,
    onStartImport: PropTypes.func.isRequired
  };

  static defaultProps = {
    buildoutOs: false
  };

  state = {
    addingLeadsToDatabase: false,
    addingToList: false,
    leadForDatabaseSelect: null,
    deletingLeadId: null,
    panelLeadId: null,
    startBulkTasks: false,
    startBulkActivities: false,
    startAddToCallList: false,
    showEmailCaModal: false,
    startNewCallList: false,
    showScrollBorder: false,
    targetCallList: null
  };

  backofficeContactIdsByLeadId = {};

  componentDidMount() {
    this.rememberBackofficeContactIds();
    this.fetchWithCurrentParams();
    window.addEventListener('storage', this.handleStorageChange);
  }

  componentWillUnmount() {
    window.removeEventListener('storage', this.handleStorageChange);
    this.debouncedSearchFetch?.cancel?.();
  }

  handleStorageChange = (event) => {
    if (event.key === 'vaultGatesToggled') {
      try {
        setTimeout(() => {
          window.location.reload();
        }, 100);
      } catch (error) {
        console.error(error);
      }
    }
  };

  componentDidUpdate(prevProps) {
    if (prevProps.leads !== this.props.leads) this.rememberBackofficeContactIds();

    const filtersChanged = !isEqual(prevProps.filters, this.props.filters);
    const sortChanged = !isEqual(prevProps.sort, this.props.sort);
    const pageChanged = prevProps.page !== this.props.page;
    const searchTextOnly = filtersChanged && this.onlySearchTextChanged(prevProps.filters, this.props.filters);

    if (searchTextOnly) {
      this.debouncedSearchFetch();
    } else if (filtersChanged || sortChanged || pageChanged) {
      this.fetchWithCurrentParams();
    }
  }

  // Search text changes happen on every keystroke; debounce so we don't hit the
  // server until the user pauses typing.
  debouncedSearchFetch = debounce(() => this.fetchWithCurrentParams(), SEARCH_DEBOUNCE_MS);

  onlySearchTextChanged = (prev, next) => {
    if (prev.searchText === next.searchText) return false;

    const keys = Object.keys({ ...prev, ...next });
    return keys.every(key => key === 'searchText' || isEqual(prev[key], next[key]));
  };

  fetchWithCurrentParams = () => {
    const { fetchLeads, project, filters, sort, page } = this.props;
    const params = buildLeadsQueryParams({
      filters,
      sort,
      page,
      perPage: RESULTS_PER_PAGE
    });
    fetchLeads(project.id, params);
  };

  anyAvailableDatabases = () => this.props.availableDatabaseOptions.length > 0;

  // Server returns the page already sorted/filtered; render in receive order.
  currentPage = () => this.props.leadsMeta.currentPage || this.props.page;

  totalPages = () => this.props.leadsMeta.totalPages || 0;

  listings() {
    const { leaseSpace, project } = this.props;
    const listings = [...project.leaseSpaceListings, ...project.lotListings, ...project.condoListings];

    if (project.listing) listings.unshift(project.listing);
    if (leaseSpace) return listings.filter(listing => listing.id === leaseSpace.listingId);

    return listings;
  }

  handleStartBulkTasks = () => {
    this.setState({ startBulkTasks: true });
  };

  handleStartBulkActivities = () => {
    this.setState({ startBulkActivities: true });
  };

  handleCloseBulkModal = () => {
    this.setState({ startBulkTasks: false, startBulkActivities: false });
  };

  handleStartAddToCallList = (list) => {
    this.setState({ startAddToCallList: true, targetCallList: list });
  };

  handleCloseAddToCallList = () => {
    this.setState({ startAddToCallList: false, targetCallList: null });
  };

  handleStartNewCallList = () => {
    this.setState({ startNewCallList: true });
  };

  handleCloseNewCallList = () => {
    this.setState({ startNewCallList: false });
  };

  setLeadForDatabaseSelect = (leadId) => {
    this.setState({ leadForDatabaseSelect: leadId });
  };

  clearLeadForDatabaseSelect = () => {
    this.setState({ leadForDatabaseSelect: null });
  };

  handleStartAddLeadsToDatabase = () => {
    this.setState({ addingLeadsToDatabase: true });
  };

  handleCloseAddLeadsToDatabase = () => {
    this.setState({ addingLeadsToDatabase: false });
  };

  addLeadToDatabase = (database, contactType, contactId) => {
    this.clearLeadForDatabaseSelect();
    return this.props.copyAndConnect(database, contactType, contactId);
  };

  addLeadsToDatabase = (database, contacts) => {
    this.handleCloseAddLeadsToDatabase();
    return this.props.bulkCopyAndConnect(database, contacts);
  };

  contactToConnect = () => {
    const lead = this.props.leads.find(l => l.id === this.state.leadForDatabaseSelect);

    if (lead) return this.props.connectableObjects.find(co => co.type === lead.contactType && co.id === lead.contactId);
  };

  contactsToConnect = () => {
    if (!this.state.addingLeadsToDatabase) return;

    const { connectableObjects, leads } = this.props;

    return connectableObjects.filter(co => (
      leads.some(lead => this.isSelected(lead.id) && co.type === lead.contactType && co.id === lead.contactId)
    ));
  };

  unlinkedLead = lead => (
    !lead.contactType ||
    (lead.contactType.includes('Company') && !lead.contactCompanyPath) ||
    !lead.contactPath
  );

  showUpdateLeadForm = (lead) => {
    this.props.showUpdateLeadModal(lead);
  };

  getLead = leadId => this.props.leads.find(lead => lead.id === leadId);

  allSelected = () => this.props.leads.every(lead => this.isSelected(lead.id));

  isSelected = leadId => this.selectedLeadIds().includes(leadId);

  selectedLeadIds = () => this.props.selectedLeads.map(l => l[0]);

  handleAddToCallList = (task) => {
    const { createBulkTask, selectedLeads } = this.props;
    const contacts = [];
    selectedLeads.forEach(lead => contacts.push([this.getLead(lead[0]).leadData.id, lead[1]]));

    createBulkTask(task, contacts);
    this.handleCloseAddToCallList();
  };

  handleNewCallList = (task) => {
    const { createCallListWithTasks, selectedLeads } = this.props;
    const contacts = [];
    selectedLeads.forEach(lead => contacts.push([this.getLead(lead[0]).leadData.id, lead[1]]));

    createCallListWithTasks(task, contacts);
    this.handleCloseNewCallList();
  };

  handleSelectLead = (lead) => {
    this.props.toggleSelectLead(lead.id, lead.contactType);
  };

  handleBulkTasks = (task) => {
    const { createBulkTask, selectedLeads } = this.props;
    const contacts = [];
    selectedLeads.forEach(lead => contacts.push([this.getLead(lead[0]).leadData.id, lead[1]]));

    createBulkTask(task, contacts);
    this.handleCloseBulkModal();
  };

  handleOpenTaskModal = (lead) => {
    this.props.showNewTaskModal({
      defaultTaskAssociation: {
        associatedModelId: lead.contactId,
        associatedModelType: lead.contactType,
        name: lead.leadData.name,
        default: true
      }
    });
  };

  handleOpenActivityLogModal = (lead) => {
    this.props.showLogTaskModal({
      defaultTaskAssociation: {
        associatedModelId: lead.contactId,
        associatedModelType: lead.contactType,
        name: lead.leadData.name,
        default: true
      }
    });
  };

  handleEmailLeads = () => {
    const emails = this.selectedLeadIds().map(id => this.getLead(id).leadData.email);

    this.props.showEmailModal(emails.filter(email => !!email));
  };

  rememberBackofficeContactIds = () => {
    this.props.leads.forEach((lead) => {
      this.backofficeContactIdsByLeadId[lead.id] = lead.backofficeContactId;
    });
  };

  selectedBackofficeContactIds = () => (
    this.selectedLeadIds().map(id => this.backofficeContactIdsByLeadId[id]).filter(Boolean)
  );

  handleStartAddToList = () => {
    if (this.selectedBackofficeContactIds().length === 0) {
      showSnackbar({ text: 'None of the selected inquiries has a CRM contact to add.', variant: 'danger' });
      return;
    }

    this.setState({ addingToList: true });
  };

  handleSetColumns = (columns) => {
    const { currentColumns, leaseSpace, setColumns } = this.props;
    if (!leaseSpace) {
      setColumns(columns);
      return;
    }

    const saved = currentColumns && !Array.isArray(currentColumns) ? currentColumns : CONFIGURABLE_COLUMNS;
    setColumns({ ...saved, ...columns, ...pick(saved, LEASE_SPACE_HIDDEN_COLUMNS) });
  };

  hiddenColumns = () => {
    const { project, features } = this.props;
    const columnsToHide = [PROPERTY];

    if (!project.leaseChecked || this.props.leaseSpace || this.shouldHideListingData()) {
      columnsToHide.push(SPACE);
    }
    // TODO: when vault gates is saved, this is stale and needs to update
    if (!project.waitlistVaultsEnabled) columnsToHide.push(WAITLIST_STATUS);
    if (!project.verifiedVaultsEnabled) columnsToHide.push(ACCOUNT_STATUS);
    // Legacy single CA_STATUS column is replaced by per-side columns in the project view.
    columnsToHide.push(CA_STATUS);
    if (!project.caRequiredVaultsEnabled) {
      columnsToHide.push(CA_STATUS_SALE);
      columnsToHide.push(CA_STATUS_LEASE);
    }
    if (!features.projectTasksActivities) columnsToHide.push(LAST_ACTIVITY);
    if (hides1031(project)) {
      columnsToHide.push(EXPIRATION_1031);
      columnsToHide.push(EXCHANGE_1031);
    }

    columnsToHide.push(...this.projectHiddenColumns(project));
    if (this.props.leaseSpace) {
      columnsToHide.push(...LEASE_SPACE_HIDDEN_COLUMNS);
    }

    return columnsToHide;
  };

  projectHiddenColumns(project) {
    const projectColumnsToHide = [];

    if (!project.leaseChecked) {
      projectColumnsToHide.push(DOC_ACCESS_LEVEL_FOR_LEASE);
      projectColumnsToHide.push(CA_STATUS_LEASE);
    }
    if (!project.saleChecked) {
      projectColumnsToHide.push(DOC_ACCESS_LEVEL_FOR_SALE);
      projectColumnsToHide.push(CA_STATUS_SALE);
    }

    return projectColumnsToHide;
  }

  defaultColumns = () => {
    const defaultColumns = { ...CONFIGURABLE_COLUMNS };
    this.hiddenColumns().forEach(column => delete defaultColumns[column]);
    if (this.props.buildoutOs) delete defaultColumns[SPACE];
    return defaultColumns;
  };

  determineCurrentColumns = () => {
    if (Array.isArray(this.props.currentColumns)) {
      return { ...CONFIGURABLE_COLUMNS };
    } else if (this.props.currentColumns === null) {
      return { ...this.defaultColumns() };
    } else {
      return { ...this.props.currentColumns };
    }
  };

  columnsToDisplay = () => {
    const currentColumns = this.determineCurrentColumns();
    this.hiddenColumns().forEach(column => currentColumns[column] = false);

    return this.props.buildoutOs ? { [SPACE]: !this.props.leaseSpace, ...omit(currentColumns, SPACE) } : currentColumns;
  };

  hasOnlySameListingLeads() {
    const leads = this.props.leads;
    const firstListing = this.listings()[0];
    return leads.every(lead => lead.listingId === firstListing.id);
  }

  shouldHideListingData = () => this.listings().length === 1 && this.hasOnlySameListingLeads();

  leadEmailLink = (leadEmail) => {
    const { project, features } = this.props;

    const links = [];
    if (project.saleListingUrl) links.push(`View Sale Listing: ${project.saleListingUrl}`);
    if (project.leaseListingUrl) links.push(`View Lease Listing: ${project.leaseListingUrl}`);

    let emailLink = `mailto:${leadEmail}?body=${encodeURIComponent(links.join('\n'))}`;
    if (features.crm) emailLink += `&bcc=${encodeURIComponent(project.forwardingEmail)}`;

    return emailLink;
  };

  getMenuIconClassName(isDisabled) {
    return `me-2 ${isDisabled ? 'text-muted' : 'text-primary'}`;
  }

  leadsReadyToDisplay() {
    return !this.props.fetchingLeads && !this.props.shouldFetchLeads;
  }

  // Once leads have loaded at least once, keep the filter/search chrome mounted even
  // while a re-fetch is in flight. Gating it on the transient `fetchingLeads` state
  // (as `leadsReadyToDisplay` does) unmounts and remounts the search input on every
  // debounced search fetch, which destroys the DOM node and steals focus mid-typing.
  leadsChromeReady() {
    return this.props.leadsMeta.loaded;
  }

  render() {
    const columnsToDisplay = this.columnsToDisplay();
    const { leadsMeta } = this.props;

    return (
      <React.Fragment>
        {this.renderHeader()}
        {this.leadsChromeReady() && (
          <LeadFilters
            currentPage={this.currentPage()}
            filters={this.props.filters}
            leadReferralSources={this.props.leadReferralSources}
            leads={this.props.leads}
            leadStatuses={this.props.leadStatuses}
            listings={this.listings()}
            pageSize={leadsMeta.pageSize || RESULTS_PER_PAGE}
            totalCount={leadsMeta.totalCount || 0}
            totalPages={this.totalPages()}
            visibleLeadCount={this.props.leads.length}
            onClearFilters={this.handleClearFilters}
            onFilterChange={this.props.handleFilterChange}
            onPageChange={this.props.setPage}
          />
        )}
        {this.props.leads.length === 0 ? this.renderEmptyState() : this.renderLeads(columnsToDisplay)}
        {this.renderPagination()}
        {this.renderBulkActivitiesOrTasksModal()}
        {this.state.startAddToCallList && this.renderAddToCallListModal()}
        {this.state.startNewCallList && this.renderNewCallListModal()}
        {this.renderSelectDatabaseModal()}
        <EmailCaModal
          caDownloadPath={this.props.caDownloadPath}
          show={this.state.showEmailCaModal}
          onClose={() => this.setState({ showEmailCaModal: false })}
        />
        {this.renderInquiryPanel()}
        {this.renderAddToListModal()}
      </React.Fragment>
    );
  }

  renderAddToListModal() {
    if (!this.state.addingToList) return null;

    const selected = this.selectedBackofficeContactIds().reduce((ids, id) => ({ ...ids, [id]: true }), {});

    return (
      <AddContactToListModal
        selection={{ selected }}
        show
        onHide={() => this.setState({ addingToList: false })}
      />
    );
  }

  renderPagination() {
    const totalPages = this.totalPages();
    return (
      <div className="py-5">
        <Pagination
          className="py-0"
          currentPage={this.currentPage()}
          totalPages={totalPages}
          onChange={this.props.setPage}
        />
      </div>
    );
  }

  renderHeader() {
    const { buildoutOs, showNewLeadModal } = this.props;

    const actions = (
      <PageHeader.Actions>
        {this.renderActionsDropdown()}
        {
          this.leadsChromeReady() &&
          <Button
            icon={buildoutOs ? faPlus : null}
            label={buildoutOs ? 'Add Inquiry' : 'Add Lead'}
            name="new-lead"
            onClick={showNewLeadModal}
          />
        }
      </PageHeader.Actions>
    );

    return <PageHeader actions={actions} title={buildoutOs ? 'Inquiries' : 'Leads'} useNewDesign={true} />;
  }

  renderEmptyState() {
    if (!this.leadsReadyToDisplay()) return <LoadingSpinner addBottomBuffer />;
    const hasActiveFilters = !isEqual(this.props.filters, this.unfilteredValue());
    const totalCount = this.props.leadsMeta.totalCount || 0;
    const noLeads = totalCount === 0 && !hasActiveFilters;
    const { buildoutOs } = this.props;

    return (
      <div
        style={{ height: '300px' }}
      >
        {noLeads ? (
          <NoLeads
            bodyText={buildoutOs
              ? 'Select the "Add Inquiry" button to create a new inquiry'
              : 'Select the "New Lead" button to create a new lead'}
            emptyLeadsImage="/images/project_empty_states/leads.svg"
            title={buildoutOs ? 'You have not created any inquiries' : undefined}
          />
        ) : (
          <LeadNoResults
            onClearFilters={this.handleClearFilters}
          />
        )}
      </div>
    );
  }

  handleClearFilters = () => {
    this.props.handleFilterChange(defaultFilterValue);
  };

  unfilteredValue() {
    const { leaseSpace } = this.props;

    return leaseSpace ? { ...defaultFilterValue, listingFilter: [leaseSpace.listingId] } : defaultFilterValue;
  }

  selectableLeadRowProps = (lead) => {
    const rowProps = {
      selected: this.isSelected(lead.id),
      onChange: () => this.handleSelectLead(lead)
    };
    if (!this.props.buildoutOs) return rowProps;

    return {
      ...rowProps,
      style: { cursor: 'pointer' },
      tabIndex: 0,
      onClick: event => this.handleRowClick(event, lead),
      onKeyDown: event => this.handleRowKeyDown(event, lead)
    };
  };

  handleRowClick = (event, lead) => {
    if (window.getSelection()?.toString()) return;
    if (!event.currentTarget.contains(event.target) || event.target.closest('a, button')) return;
    if (event.target.closest('td')?.querySelector('input, button, [role="combobox"]')) return;

    this.setState({ panelLeadId: lead.id });
  };

  handleRowKeyDown = (event, lead) => {
    if (event.target !== event.currentTarget || (event.key !== 'Enter' && event.key !== ' ')) return;

    event.preventDefault();
    this.setState({ panelLeadId: lead.id });
  };

  closePanel = () => {
    this.setState({ panelLeadId: null });
  };

  deletePanelLead = (lead) => {
    const { removeLead, project } = this.props;
    this.setState({ deletingLeadId: lead.id });
    removeLead(project.id, lead, () => {
      this.setState(state => ({
        deletingLeadId: null,
        panelLeadId: state.panelLeadId === lead.id ? null : state.panelLeadId
      }));
    }, () => {
      this.setState({ deletingLeadId: null });
      showSnackbar({ text: 'Something went wrong deleting this inquiry.', variant: 'danger' });
    });
  };

  renderInquiryPanel() {
    if (!this.props.buildoutOs) return null;

    const { canDisplayEmailLink, features, leads, project, refreshLead, showUpdateLeadModal } = this.props;
    const index = leads.findIndex(lead => lead.id === this.state.panelLeadId);
    if (index === -1) return null;

    const panelLead = leads[index];
    const hiddenColumns = this.hiddenColumns();
    const pager = {
      count: leads.length,
      position: index + 1,
      onNext: () => this.setState({ panelLeadId: leads[index + 1].id }),
      onPrevious: () => this.setState({ panelLeadId: leads[index - 1].id })
    };

    return (
      <InquiryPanel
        canDisplayEmailLink={canDisplayEmailLink}
        deleting={this.state.deletingLeadId === panelLead.id}
        features={features}
        lead={panelLead}
        leaseChecked={!!project.leaseChecked}
        leaseOnly={!!this.props.leaseSpace}
        pager={pager}
        project={project}
        refreshLead={refreshLead}
        saleChecked={!!project.saleChecked}
        show1031={!hiddenColumns.includes(EXCHANGE_1031)}
        showSpace={!hiddenColumns.includes(SPACE)}
        showUpdateLeadModal={showUpdateLeadModal}
        onClose={this.closePanel}
        onDelete={this.deletePanelLead}
      />
    );
  }

  renderLeads(columnsToDisplay = {}) {
    const headerOverrides = {};
    if (this.props.useVaultGates) {
      const baseTitle = 'Document Progress';
      const hasSaleDocAccess = columnsToDisplay[DOC_ACCESS_LEVEL_FOR_SALE] === true;
      const hasLeaseDocAccess = columnsToDisplay[DOC_ACCESS_LEVEL_FOR_LEASE] === true;

      if (hasSaleDocAccess) {
        headerOverrides[DOC_ACCESS_LEVEL_FOR_SALE] = `${hasLeaseDocAccess ? 'Sale ' : ''}${baseTitle}`;
      }
      if (hasLeaseDocAccess) {
        headerOverrides[DOC_ACCESS_LEVEL_FOR_LEASE] = `${hasSaleDocAccess ? 'Lease ' : ''}${baseTitle}`;
      }
    }

    const baseCaTitle = 'CA Status';
    const hasSaleCa = columnsToDisplay[CA_STATUS_SALE] === true;
    const hasLeaseCa = columnsToDisplay[CA_STATUS_LEASE] === true;
    if (hasSaleCa) {
      headerOverrides[CA_STATUS_SALE] = `${hasLeaseCa ? 'Sale ' : ''}${baseCaTitle}`;
    }
    if (hasLeaseCa) {
      headerOverrides[CA_STATUS_LEASE] = `${hasSaleCa ? 'Lease ' : ''}${baseCaTitle}`;
    }

    return (
      <div className={this.props.buildoutOs ? 'os-leads-table' : undefined}>
        <LeadsTable
          cellOverrides={{ [NAME]: LeadName }}
          clientReportActive={this.props.clientReportActive}
          columns={columnsToDisplay}
          columnTitles={this.props.buildoutOs ? { [LEAD_STATUS]: 'Inquiry Status' } : undefined}
          columnTooltips={this.props.buildoutOs ? {
            [LEAD_STATUS]: 'Inquiry Status listed is manually updated and the options are configurable within ' +
              'Company Settings > Projects.'
          } : undefined}
          defaultColumns={this.defaultColumns()}
          headerOverrides={headerOverrides}
          leads={this.props.leads}
          renderLeadOptions={this.renderLeadOptions}
          selectableLeadRowProps={this.selectableLeadRowProps}
          selectAllChecked={this.allSelected()}
          sortColumn={this.props.sort.field}
          sortDesc={this.props.sort.reverse}
          stickyColumns={2}
          onSelectAllToggle={this.props.toggleSelectAllLeads}
          onSetColumns={this.handleSetColumns}
          onSort={this.props.changeSort}
        />
      </div>
    );
  }

  renderActionsDropdown() {
    const {
      buildoutOs, caDownloadPath, fromDealTab, leadsPath, leaseSpace, onStartImport, features, filters,
    } = this.props;
    if (!this.leadsChromeReady()) return;

    const noLeadsSelect = this.props.selectedLeads.length === 0;
    // The Apex deal tab has never offered CA downloads or imports. Buildout OS does, the same way Showcase
    // does, even though it renders through that tab, but not on a lease space's tab: both are project-wide
    // (an import there would not land on the space, and the CA export would cover the whole project).
    const canDownloadAndImport = (buildoutOs && !leaseSpace) || !fromDealTab;

    return (
      <DropdownButton title={<><span>Actions</span><FontAwesomeIcon icon={faCaretDown} /></>} variant="outline">
        {this.props.hasTasksEnabled &&
          <React.Fragment>
            <Dropdown.Item
              disabled={noLeadsSelect}
              key="bulk-tasks"
              name="bulk-tasks"
              onClick={noLeadsSelect ? null : this.handleStartBulkTasks}
            >
              <FontAwesomeIcon className={this.getMenuIconClassName(noLeadsSelect)}
                fixedWidth icon={faCheck} size="sm"
              /> New Task
            </Dropdown.Item>
            <Dropdown.Item
              disabled={noLeadsSelect}
              key="bulk-activities"
              name="bulk-activities"
              onClick={noLeadsSelect ? null : this.handleStartBulkActivities}
            >
              <FontAwesomeIcon className={this.getMenuIconClassName(noLeadsSelect)}
                fixedWidth icon={faBolt} size="sm"
              /> New Activity
            </Dropdown.Item>
          </React.Fragment>
        }
        {features.crm &&
          <Dropdown.Item
            disabled={noLeadsSelect}
            key="add-leads-to-database"
            name="add-leads-to-database"
            onClick={noLeadsSelect ? null : this.handleStartAddLeadsToDatabase}
          >
            <FontAwesomeIcon className={this.getMenuIconClassName(noLeadsSelect)}
              fixedWidth icon={faUserPlus} size="sm"
            /> Add to Database
          </Dropdown.Item>
        }
        {/* Buildout OS leaves email to Showcase. */}
        {this.props.canDisplayEmailLink && !buildoutOs &&
          <Dropdown.Item
            disabled={noLeadsSelect}
            key="email-leads"
            name="email-leads"
            onClick={noLeadsSelect ? null : this.handleEmailLeads}
          >
            <FontAwesomeIcon className={this.getMenuIconClassName(noLeadsSelect)}
              fixedWidth icon={faEnvelope} size="sm"
            /> Email
          </Dropdown.Item>
        }
        {buildoutOs && features.addLeadsToLists &&
          <Dropdown.Item
            disabled={noLeadsSelect}
            key="add-to-list"
            name="add-to-list"
            onClick={noLeadsSelect ? null : this.handleStartAddToList}
          >
            <FontAwesomeIcon className={this.getMenuIconClassName(noLeadsSelect)}
              fixedWidth icon={faRectangleHistoryCirclePlus} size="sm"
            /> Add to List
          </Dropdown.Item>
        }
        {leadsPath && <CsvDownloadButton filters={filters} href={`${leadsPath}.csv`} type="MenuItem" />}
        {canDownloadAndImport && caDownloadPath && this.renderCaDownloadOption()}
        {canDownloadAndImport && (
          <Dropdown.Item
            disabled={!noLeadsSelect}
            key="import-leads"
            name="import-leads"
            onClick={noLeadsSelect ? onStartImport : null}
          >
            <FontAwesomeIcon className={this.getMenuIconClassName(!noLeadsSelect)}
              fixedWidth icon={faUserFriends} size="sm"
            /> Import
          </Dropdown.Item>
        )}
        {this.renderCallListOptions(noLeadsSelect)}
        <Dropdown.Divider />
        <Dropdown.Item
          disabled={noLeadsSelect}
          key="bulk-delete"
          name="bulk-delete"
          onClick={noLeadsSelect ? null : this.confirmBulkDelete}
        >
          <FontAwesomeIcon className={this.getMenuIconClassName(noLeadsSelect)}
            fixedWidth icon={faTrashAlt} size="sm"
          /> Delete
        </Dropdown.Item>
      </DropdownButton>
    );
  }

  renderCaDownloadOption = () => {
    const { caDownloadPath, emailCa } = this.props;
    const messageForMenuItem = 'Download CA Activity';

    if (!emailCa) {
      return <CsvDownloadButton href={caDownloadPath} messageOverride={messageForMenuItem} type="MenuItem" />;
    }

    return (
      <Dropdown.Item
        key="download-ca-data"
        onClick={() => this.setState({ showEmailCaModal: true })}
      >
        <FontAwesomeIcon className="me-2 text-primary" fixedWidth icon={faDownload} size="sm" />
        {messageForMenuItem}
      </Dropdown.Item>
    );
  };

  renderLeadOptions = (lead) => {
    const { buildoutOs, features } = this.props;

    return (
      <Dropdown>
        <Dropdown.Toggle id={`dropdown-${lead.id}`} variant="ghost">
          <FontAwesomeIcon className="clickable" icon={faEllipsisV} size="lg" style={{ color: 'initial' }} />
        </Dropdown.Toggle>
        {createPortal(
          <Dropdown.Menu>
            {this.props.hasTasksEnabled &&
              <React.Fragment>
                <Dropdown.Item
                  eventKey="1"
                  name="new-task"
                  onClick={() => this.handleOpenTaskModal(lead)}
                >
                  <FontAwesomeIcon className={this.getMenuIconClassName()}
                    fixedWidth icon={faCheck} size="sm"
                  /> New Task
                </Dropdown.Item>
                <Dropdown.Item
                  eventKey="5"
                  name="new-activity"
                  onClick={() => this.handleOpenActivityLogModal(lead)}
                >
                  <FontAwesomeIcon className={this.getMenuIconClassName()}
                    fixedWidth icon={faBolt} size="sm"
                  /> New Activity
                </Dropdown.Item>
              </React.Fragment>
            }
            {this.anyAvailableDatabases() && this.unlinkedLead(lead) && features.crm && (
              <Dropdown.Item
                eventKey="4"
                name="add-to-database"
                onClick={() => this.setLeadForDatabaseSelect(lead.id)}
              >
                <FontAwesomeIcon className={this.getMenuIconClassName()}
                  fixedWidth icon={faUserPlus} size="sm"
                /> Add to Database
              </Dropdown.Item>
            )}
            <AccessOptions
              approveLeadVaultAccess={this.props.approveLeadVaultAccess}
              blockLeadVaultAccess={this.props.blockLeadVaultAccess}
              currentUser={this.props.currentUser}
              iconClassName={this.getMenuIconClassName()}
              lead={lead}
              refreshLead={this.props.refreshLead}
              revokeLeadVaultAccess={this.props.revokeLeadVaultAccess}
              unblockLeadVaultAccess={this.props.unblockLeadVaultAccess}
            />
            {this.props.canDisplayEmailLink && lead.leadData.email && (
              <Dropdown.Item
                eventKey="5"
                href={this.leadEmailLink(lead.leadData.email)}
                name="email-lead"
                rel="noopener noreferrer"
                target="_blank"
              >
                <FontAwesomeIcon className={this.getMenuIconClassName()} fixedWidth icon={faEnvelope} size="sm" /> Email
              </Dropdown.Item>
            )}
            {!buildoutOs && <Dropdown.Divider />}
            <Dropdown.Item
              eventKey="2"
              name="edit-lead"
              onClick={() => this.showUpdateLeadForm(lead)}
            >
              <FontAwesomeIcon className={this.getMenuIconClassName()} fixedWidth icon={faPencilAlt} size="sm" /> Edit
            </Dropdown.Item>
            {buildoutOs && <Dropdown.Divider />}
            <Dropdown.Item
              eventKey="3"
              name="delete-lead"
              onClick={() => this.confirmDelete(lead)}
            >
              <FontAwesomeIcon className={this.getMenuIconClassName()} fixedWidth icon={faTrashAlt} size="sm" />
              {buildoutOs ? ' Remove' : ' Delete'}
            </Dropdown.Item>
          </Dropdown.Menu>
          , document.body)}
      </Dropdown>
    );
  };

  renderSelectDatabaseModal() {
    const connectableObject = this.contactToConnect();
    const connectableObjects = connectableObject ? null : this.contactsToConnect();

    return (
      <ConnectionLinkModal
        availableDatabaseOptions={this.props.availableDatabaseOptions}
        connectableObject={connectableObject}
        connectableObjects={connectableObjects}
        onCopyAndConnect={connectableObjects ? this.addLeadsToDatabase : this.addLeadToDatabase}
        onHide={connectableObjects ? this.handleCloseAddLeadsToDatabase : this.clearLeadForDatabaseSelect}
      />
    );
  }

  renderCallListOptions(noLeadsSelect) {
    const { callLists, callListsEnabled, features } = this.props;
    if (!callListsEnabled || !features.crm) return null;

    const sortedCallLists = sortBy(callLists, list => list.name);
    const listOptions = map(sortedCallLists, list => (
      <Dropdown.Item
        key={list.id}
        onClick={() => this.handleStartAddToCallList(list)}
      >
        {list.name}
      </Dropdown.Item>
    ));

    const showAddLink = callLists.length > 0;

    return (
      <React.Fragment>
        <Dropdown.Item disabled={noLeadsSelect} name="create-call-list" onClick={this.handleStartNewCallList}>
          <FontAwesomeIcon
            className={this.getMenuIconClassName(noLeadsSelect)}
            fixedWidth icon={faPhonePlus}
            size="sm"
          />
          Create Call List
        </Dropdown.Item>
        {showAddLink &&
          <li className={`dropdown-submenu to-left-compact ${noLeadsSelect ? 'disabled' : ''}`}
            style={{ pointerEvents: noLeadsSelect ? 'none' : 'auto' }}
          >
            <Dropdown>
              <Dropdown.Toggle as="a" className="dropdown-item py-2 px-3">
                <FontAwesomeIcon
                  className={this.getMenuIconClassName(noLeadsSelect)}
                  fixedWidth icon={faPhonePlus}
                  size="sm"
                />
                Add to Call List
                <FontAwesomeIcon className="text-muted" icon={faCaretRight} />
              </Dropdown.Toggle>
              <Dropdown.Menu>{listOptions}</Dropdown.Menu>
            </Dropdown>
          </li>
        }
      </React.Fragment>
    );
  }

  renderBulkActivitiesOrTasksModal() {
    const { availableDatabaseOptions, defaultTaskAssignee, databaseMembers, project } = this.props;
    const { startBulkActivities, startBulkTasks } = this.state;

    if (!(startBulkActivities || startBulkTasks)) return null;

    const selectedContacts = [];
    this.selectedLeadIds().forEach(id => selectedContacts.push(this.getLead(id).leadData));

    const defaultAssociatedModels = [
      {
        associatedModelId: 1,
        associatedModelType: 'Multiple',
        name: 'Multiple Contacts',
        default: true
      },
      {
        associatedModelId: project.id,
        associatedModelType: 'Project',
        name: project.name,
        default: true
      }
    ];

    return (
      <BulkTasksModal
        availableDatabases={availableDatabaseOptions}
        currentUser={defaultTaskAssignee}
        databaseMembers={databaseMembers}
        defaultAssociatedModels={defaultAssociatedModels}
        isLogged={startBulkActivities}
        selectedContacts={selectedContacts}
        shown={true}
        onCancel={this.handleCloseBulkModal}
        onSubmit={this.handleBulkTasks}
      />
    );
  }

  renderAddToCallListModal() {
    const { availableDatabaseOptions, defaultTaskAssignee, databaseMembers, project } = this.props;
    const { startAddToCallList, targetCallList } = this.state;

    const selectedContacts = [];
    this.selectedLeadIds().forEach(id => selectedContacts.push(this.getLead(id).leadData));

    const defaultAssociatedModels = [
      {
        associatedModelId: 1,
        associatedModelType: 'Multiple',
        name: 'Multiple Contacts',
        default: true
      },
      {
        associatedModelId: project.id,
        associatedModelType: 'Project',
        name: project.name,
        default: true
      }
    ];

    return (
      <AddToCallListModal
        availableDatabases={availableDatabaseOptions}
        callList={targetCallList}
        currentUser={defaultTaskAssignee}
        databaseMembers={databaseMembers}
        defaultAssociatedModels={defaultAssociatedModels}
        selectedContacts={selectedContacts}
        shown={startAddToCallList}
        onCancel={this.handleCloseAddToCallList}
        onSubmit={this.handleAddToCallList}
      />
    );
  }

  renderNewCallListModal() {
    const { availableDatabaseOptions, defaultTaskAssignee, databaseMembers, project } = this.props;
    const { startNewCallList } = this.state;

    const selectedContacts = [];
    this.selectedLeadIds().forEach(id => selectedContacts.push(this.getLead(id).leadData));

    const defaultAssociatedModels = [
      {
        associatedModelId: 1,
        associatedModelType: 'Multiple',
        name: 'Multiple Contacts',
        default: true
      },
      {
        associatedModelId: project.id,
        associatedModelType: 'Project',
        name: project.name,
        default: true
      }
    ];

    return (
      <NewCallListModal
        availableDatabases={availableDatabaseOptions}
        currentUser={defaultTaskAssignee}
        databaseMembers={databaseMembers}
        defaultAssociatedModels={defaultAssociatedModels}
        selectedContacts={selectedContacts}
        shown={startNewCallList}
        onCancel={this.handleCloseNewCallList}
        onSubmit={this.handleNewCallList}
      />
    );
  }

  confirmBulkDelete = () => {
    const numLeads = this.props.selectedLeads.length;
    if (numLeads === 0) return;

    const { buildoutOs } = this.props;
    const message = `Are you sure you want to remove ${pluralize('this', numLeads)}
      ${pluralize(buildoutOs ? 'inquiry' : 'lead', numLeads, true)} from this project?`;
    confirm(message, {
      // Buildout OS's menu item and title say Delete, so its button does too.
      buttonLabel: buildoutOs ? 'Delete' : 'Remove',
      buttonStyle: 'danger',
      title: `Delete ${pluralize(buildoutOs ? 'Inquiry' : 'Lead', numLeads)}`
    }).then(() => {
      const { bulkRemoveLeads, project } = this.props;
      bulkRemoveLeads(project.id, this.selectedLeadIds());
    });
  };

  confirmDelete = (lead) => {
    const { buildoutOs } = this.props;
    const message = `Are you sure you want to remove this ${buildoutOs ? 'inquiry' : 'lead'}?`;
    confirm(message, {
      buttonLabel: 'Remove',
      buttonStyle: 'danger',
      title: buildoutOs ? 'Remove Inquiry' : 'Delete Lead'
    }).then(() => {
      const { removeLead, project } = this.props;
      removeLead(project.id, lead);
    });
  };
}

const mapStateToProps = state => ({
  availableDatabaseOptions: getAvailableDatabaseOptions(state),
  connectableObjects: getConnectableObjects(state),
  currentColumns: state.session.currentColumns,
  currentUser: state.currentUser,
  features: state.features,
  fetchingLeads: isFetchingLeads(state),
  leads: getDecoratedLeads(state),
  leadsMeta: getLeadsMeta(state),
  project: getProject(state),
  selectedLeads: getSelectedLeads(state),
  shouldFetchLeads: shouldFetchLeads(state)
});

const actions = {
  approveLeadVaultAccess,
  blockLeadVaultAccess,
  bulkCopyAndConnect,
  bulkRemoveLeads,
  copyAndConnect,
  createBulkTask,
  createCallListWithTasks,
  fetchLeads,
  refreshLead,
  removeLead,
  revokeLeadVaultAccess,
  setColumns,
  showEmailModal,
  showUpdateLeadModal,
  showLogTaskModal,
  showNewLeadModal,
  showNewTaskModal,
  toggleSelectAllLeads,
  toggleSelectLead,
  unblockLeadVaultAccess
};

const ConnectedLeadsList = connect(mapStateToProps, actions)(LeadsList);

export default function LeadsListWithDisplay(props) {
  return <ConnectedLeadsList {...props} buildoutOs={useBuildoutOsDisplay()} />;
}
